import { useState, useContext, useEffect, useMemo } from 'react';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import MaterialTable from 'material-table';
import PatchedPagination from './PatchedPagination';
import { FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText } from '@material-ui/core';
import { serverContext, appContext, postToServer, putToServer, callServer } from '../App';
import moment from 'moment';
import { useGetUsergroups } from '../customEffects';
import { securityClassificationMap, securityClassificationLookup } from '../serverReducer';
import { tableIcons } from '../commonTypes';
import PropTypes from 'prop-types';
import { useTableTheming } from './useTableTheming';

function showError(dispatchApp, title, error) {
  dispatchApp({
    type: 'SHOW_ALERT',
    payload: {
      title,
      message: `Error: ${error?.response?.data ?? error.message ?? error}`,
    },
  });
}

const translateUserGroups = usergroups => {
  if (!Array.isArray(usergroups)) return [];

  let i = 0;
  return usergroups.map(u => ({
    id: i++,
    usergroupId: u._id,
    name: u.name,
    description: u.description,
    liveModes: u.liveModes || [],
    securityClassification: securityClassificationMap[u.securityClassification],
    created: u.created,
  }));
};

// Available options for live modes
const liveModeOptions = ['Preview', 'WebRtc'];

const UserGroupsTable = ({ title }) => {
  const { server } = useContext(serverContext);
  const { dispatchApp } = useContext(appContext);
  const [selectedRow, setSelectedRow] = useState(null);
  const tableTheme = useTableTheming(selectedRow);
  const [data, setData] = useState([]);

  useGetUsergroups();

  // Responsive breakpoint
  const isSmall = useMediaQuery('(max-width:959px)');

  const columns = useMemo(
    () => [
      { title: 'Name', field: 'name', width: '18%', tooltip: 'Usergroup name' },
      { title: 'Description', field: 'description', tooltip: 'Usergroup description' },
      {
        title: 'Classification',
        field: 'securityClassification',
        width: '10%',
        tooltip: 'Usergroup security classification',
        lookup: securityClassificationLookup,
      },
      {
        title: 'Live modes',
        field: 'liveModes',
        width: '16%',
        tooltip: 'Allowed live modes',
        render: rowData => (rowData.liveModes.length ? rowData.liveModes.join(', ') : '-'),
        editComponent: props => (
          <FormControl   style={{ minWidth: '120px' }}>
            <InputLabel>Live Modes</InputLabel>
            <Select
              label="Live Modes"            
              multiple
              value={props.value || []}
              onChange={e => props.onChange(e.target.value)}
              renderValue={selected => selected.join(', ')}
            >
              {liveModeOptions.map(mode => (
                <MenuItem key={mode} value={mode}>
                  <Checkbox checked={(props.value || []).includes(mode)} />
                  <ListItemText primary={mode} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ),
        hidden: isSmall,
      },
      {
        title: 'Created',
        field: 'created',
        width: '18%',
        tooltip: 'Usergroup creation date',
        editable: 'never',
        render: rowData => (rowData.created ? moment(rowData.created).format('MMM Do YY, HH:mm') : '-'),
        hidden: isSmall,
      },
    ],
    [isSmall]
  );

  useEffect(() => {
    setData(translateUserGroups(server.usergroups));
  }, [server.usergroups]);

  const actions = [
    {
      icon: tableIcons.DeleteIcon,
      tooltip: 'Delete Usergroup(s)',
      onClick: async (_, rows) => {
        try {
          const names = rows.map(r => r.name).join(',');
          await callServer('delete', `usergroups?usergroups=${encodeURIComponent(names)}`, undefined, server);
        } catch (error) {
          showError(dispatchApp, 'Error deleting usergroup(s)', error);
        }
      },
    },
  ];

  const handleRowUpdate = (newData, oldData) =>
    new Promise(async (resolve, reject) => {
      try {
        const liveModesStr = (newData.liveModes || []).join(',');
        await putToServer(
          `usergroups/${newData.usergroupId}?name=${encodeURIComponent(newData.name)}` +
            `&description=${encodeURIComponent(newData.description)}` +
            `&securityClassification=${encodeURIComponent(
              securityClassificationLookup[newData.securityClassification]
            )}` +
            `&liveModes=${encodeURIComponent(liveModesStr)}`,
          {},
          server
        );
        resolve();
      } catch (error) {
        showError(dispatchApp, 'Error updating usergroup', error);
        reject();
      }
    });

  const handleRowAdd = async newData => {
    try {
      await postToServer(
        'usergroups',
        {
          name: newData.name,
          description: newData.description,
          securityClassification: newData.securityClassification
            ? securityClassificationLookup[newData.securityClassification]
            : 'UNCLASSIFIED',
          liveModes: newData.liveModes || [],
        },
        server
      );
    } catch (error) {
      showError(dispatchApp, 'Error adding usergroup', error);
      throw error;
    }
  };

  return (
    <MaterialTable
      icons={tableIcons}
      columns={columns}
      data={data}
      title={title || 'User Groups'}
      onRowClick={(_, selected) => setSelectedRow(selected)}
      options={{
        selection: true,
        cellStyle: tableTheme.cellStyle,
        headerStyle: tableTheme.headerStyle,
        filterRowStyle: tableTheme.filterRowStyle,
        rowStyle: tableTheme.rowStyle,
      }}
      actions={actions}
      editable={{
        isEditable: rowData => rowData.name !== 'Demo',
        isDeletable: rowData => rowData.name !== 'Demo',
        onRowAdd: handleRowAdd,
        onRowUpdate: handleRowUpdate,
      }}
      components={{
        Pagination: PatchedPagination,
      }}
    />
  );
};

UserGroupsTable.propTypes = {
  title: PropTypes.string,
};

export default UserGroupsTable;
